import { useEffect } from 'react';

interface StructuredDataProps {
  type?: 'home' | 'faq';
}

const StructuredData = ({ type = 'home' }: StructuredDataProps) => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.vaciarlocales.net';

    // Organization Schema
    const organizationData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "ECOSERVEIS",
      "legalName": "ECOSERVEIS - Vaciado de Locales Barcelona",
      "url": siteUrl,
      "logo": `${siteUrl}/logo.png`,
      "image": `${siteUrl}/logo.png`,
      "description": "Empresa especializada en vaciado de locales comerciales, naves industriales y oficinas en Barcelona. Servicio profesional con gestión de residuos incluida.",
      "telephone": "+34644613451",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Av. Nord 11 B-2",
        "addressLocality": "L'Hospitalet de Llobregat",
        "addressRegion": "Barcelona",
        "postalCode": "08905",
        "addressCountry": "ES"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 41.3588,
        "longitude": 2.1098
      },
      "areaServed": [
        {
          "@type": "City",
          "name": "Barcelona"
        },
        {
          "@type": "City",
          "name": "L'Hospitalet de Llobregat"
        },
        {
          "@type": "City",
          "name": "Badalona"
        },
        {
          "@type": "City",
          "name": "Terrassa"
        },
        {
          "@type": "City",
          "name": "Sabadell"
        },
        {
          "@type": "City",
          "name": "Mataró"
        }
      ],
      "sameAs": [
        "https://maps.app.goo.gl/kGNXARTnHrknyJRDA"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+34644613451",
        "contactType": "customer service",
        "areaServed": "ES",
        "availableLanguage": ["Spanish", "Catalan"]
      }
    };

    // LocalBusiness Schema
    const localBusinessData = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "ECOSERVEIS",
      "image": `${siteUrl}/logo.png`,
      "url": siteUrl,
      "telephone": "+34644613451",
      "priceRange": "€€",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Av. Nord 11 B-2",
        "addressLocality": "L'Hospitalet de Llobregat",
        "addressRegion": "Barcelona",
        "postalCode": "08905",
        "addressCountry": "ES"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 41.3588,
        "longitude": 2.1098
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday"
          ],
          "opens": "09:00",
          "closes": "18:00"
        }
      ],
      "areaServed": [
        {
          "@type": "City",
          "name": "Barcelona"
        },
        {
          "@type": "City",
          "name": "L'Hospitalet de Llobregat"
        },
        {
          "@type": "City",
          "name": "Badalona"
        },
        {
          "@type": "City",
          "name": "Terrassa"
        },
        {
          "@type": "City",
          "name": "Sabadell"
        }
      ],
      "sameAs": "https://maps.app.goo.gl/kGNXARTnHrknyJRDA",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5",
        "reviewCount": "127"
      }
    };

    // Service Schema
    const serviceData = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Vaciado de Locales Comerciales",
      "provider": {
        "@type": "LocalBusiness",
        "name": "ECOSERVEIS",
        "telephone": "+34644613451",
        "url": siteUrl
      },
      "areaServed": {
        "@type": "City",
        "name": "Barcelona"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Servicios de Vaciado",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Vaciado de Locales Comerciales",
              "description": "Servicio profesional de vaciado de locales comerciales en Barcelona con retirada de mobiliario y gestión de residuos"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Vaciado de Naves Industriales",
              "description": "Vaciado completo de naves industriales y almacenes con retirada de estanterías y palets"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Vaciado de Oficinas",
              "description": "Vaciado profesional de oficinas corporativas con desmontaje de mamparas y mobiliario"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Retirada de Mobiliario Comercial",
              "description": "Retirada y transporte de mobiliario comercial fijo y móvil"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Desmontaje de Estanterías",
              "description": "Desmontaje profesional de estanterías, mostradores y mamparas"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Gestión de Residuos",
              "description": "Gestión legal y responsable de residuos con certificación"
            }
          }
        ]
      }
    };

    // BreadcrumbList Schema
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Inicio",
          "item": `${siteUrl}/`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Empresa",
          "item": `${siteUrl}/empresa`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": "Servicios",
          "item": `${siteUrl}/servicios`
        },
        {
          "@type": "ListItem",
          "position": 4,
          "name": "FAQ",
          "item": `${siteUrl}/faq`
        },
        {
          "@type": "ListItem",
          "position": 5,
          "name": "Contacto",
          "item": `${siteUrl}/contacto`
        }
      ]
    };

    // WebSite Schema
    const websiteData = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "ECOSERVEIS - Vaciado de Locales Barcelona",
      "url": siteUrl,
      "description": "Empresa especializada en vaciado de locales comerciales, naves industriales y oficinas en Barcelona",
      "publisher": {
        "@type": "Organization",
        "name": "ECOSERVEIS"
      },
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${siteUrl}/?s={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    };

    // FAQ Schema
    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "¿En cuánto tiempo pueden realizar el vaciado?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Según disponibilidad, podemos realizar el servicio en 24-48 horas. El tiempo exacto depende del volumen a retirar y la complejidad del trabajo. Para vaciados urgentes, contacta directamente por teléfono y verificaremos disponibilidad inmediata. En casos de grandes volúmenes o trabajos complejos, el plazo puede extenderse, pero siempre te informaremos con antelación del tiempo estimado."
          }
        },
        {
          "@type": "Question",
          "name": "¿Cómo se calcula el precio del servicio?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "El precio depende de varios factores: volumen de material a retirar, tipo de enseres (mobiliario ligero o pesado), accesibilidad del local (planta, ascensor, ancho de puertas), necesidad de desmontajes especiales, y si requiere limpieza final. Ofrecemos presupuestos sin compromiso tras una visita presencial o valoración fotográfica detallada. Nuestros precios son transparentes y sin sorpresas."
          }
        },
        {
          "@type": "Question",
          "name": "¿Qué tipos de inmuebles vacían?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Vaciamos locales comerciales (tiendas, bares, restaurantes), naves industriales y almacenes, oficinas y despachos, trasteros, garajes, y cualquier espacio comercial o industrial. No realizamos vaciados de viviendas particulares. Nuestro servicio está especializado en espacios comerciales e industriales de todo tipo y tamaño."
          }
        },
        {
          "@type": "Question",
          "name": "¿Necesito permisos especiales para el vaciado?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "En la mayoría de casos no son necesarios permisos especiales para el vaciado interior. Si se requiere ocupación de vía pública con contenedores o vehículos durante tiempo prolongado, nosotros gestionamos los permisos necesarios con el ayuntamiento correspondiente. Este trámite está incluido en nuestro servicio cuando es necesario. Te informaremos si tu caso requiere permisos adicionales."
          }
        },
        {
          "@type": "Question",
          "name": "¿Qué pasa si el local tiene difícil acceso?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Trabajamos habitualmente en locales con todo tipo de accesos: sótanos, plantas altas sin ascensor, calles estrechas del casco antiguo, locales con puertas pequeñas, etc. Evaluamos cada caso durante la visita previa y adaptamos nuestro equipo y método de trabajo a las condiciones específicas. Contamos con experiencia en accesos complicados en toda Barcelona y provincia."
          }
        },
        {
          "@type": "Question",
          "name": "¿En qué horario trabajan?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Nos adaptamos a tus necesidades y las normativas de la comunidad de vecinos. Podemos trabajar en horario comercial estándar, fines de semana, o en horarios especiales según disponibilidad y coordinación previa. Si tu comunidad tiene restricciones de horario para trabajos, nos ajustamos a ellas. Consúltanos tu caso particular y encontraremos la mejor solución."
          }
        },
        {
          "@type": "Question",
          "name": "¿Realizan desmontajes de mobiliario fijo?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sí, realizamos desmontajes no estructurales como estanterías metálicas y de madera, mostradores, barras, mamparas de oficina, falsos techos accesibles, y elementos de carpintería no estructural. No realizamos obras ni desmontajes que afecten a la estructura del edificio, instalaciones eléctricas principales o fontanería. Si tienes dudas sobre un desmontaje específico, consúltanos."
          }
        },
        {
          "@type": "Question",
          "name": "¿Cómo gestionan los residuos?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Gestionamos todos los residuos de forma legal y responsable cumpliendo la normativa ambiental vigente. Separamos materiales reciclables (metal, madera, plástico, cartón), llevamos enseres reutilizables a puntos de donación cuando es posible, y depositamos el resto en plantas de tratamiento autorizadas. Podemos entregar certificados de gestión de residuos si los necesitas para trámites administrativos."
          }
        },
        {
          "@type": "Question",
          "name": "¿Tienen seguro de responsabilidad civil?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sí, todos nuestros servicios están cubiertos por un seguro de responsabilidad civil. Incluimos el número de póliza en todos nuestros presupuestos por escrito. Esto te protege ante cualquier incidencia durante el servicio. Es importante que exijas este dato a cualquier empresa de vaciados para evitar sorpresas desagradables."
          }
        },
        {
          "@type": "Question",
          "name": "¿Entregan factura del servicio?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sí, emitimos factura completa de todos nuestros servicios. Somos una empresa legalmente constituida y cumplimos con todas las obligaciones fiscales. La factura incluye el detalle del servicio realizado, gestión de residuos, y cualquier trabajo adicional acordado. Puedes deducir el gasto si es para actividad empresarial o profesional."
          }
        },
        {
          "@type": "Question",
          "name": "¿Recogen un solo objeto o mueble suelto?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No, nuestro servicio mínimo es el equivalente a una furgoneta llena. Estamos especializados en vaciados completos de locales, naves y oficinas, no en recogida de objetos individuales. Este servicio mínimo nos permite mantener precios competitivos en vaciados completos. Si necesitas retirar solo algunos objetos, te recomendamos contactar con servicios de recogida de muebles específicos."
          }
        },
        {
          "@type": "Question",
          "name": "¿Ofrecen servicio de limpieza final?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sí, ofrecemos limpieza final opcional tras el vaciado. Este servicio incluye barrido, fregado de suelos, limpieza de polvo y residuos, limpieza de cristales, y retirada de adhesivos y marcas. Dejamos el espacio completamente limpio y listo para entrega de llaves, reforma o nuevo uso. Consúltanos para incluir la limpieza en tu presupuesto personalizado."
          }
        }
      ]
    };

    // Limpiar scripts anteriores
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Insertar nuevos scripts
    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.text = JSON.stringify(organizationData);
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.text = JSON.stringify(localBusinessData);
    document.head.appendChild(script2);

    const script3 = document.createElement('script');
    script3.type = 'application/ld+json';
    script3.text = JSON.stringify(serviceData);
    document.head.appendChild(script3);

    const script4 = document.createElement('script');
    script4.type = 'application/ld+json';
    script4.text = JSON.stringify(breadcrumbData);
    document.head.appendChild(script4);

    const script5 = document.createElement('script');
    script5.type = 'application/ld+json';
    script5.text = JSON.stringify(websiteData);
    document.head.appendChild(script5);

    if (type === 'faq') {
      const script6 = document.createElement('script');
      script6.type = 'application/ld+json';
      script6.text = JSON.stringify(faqData);
      document.head.appendChild(script6);
    }

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => script.remove());
    };
  }, [type]);

  return null;
};

export default StructuredData;
